/**
 * Fait : LA fenêtre de confirmation des gestes que rien ne rattrape. Un bouton
 *   ouvre la fenêtre ; la fenêtre porte l'avertissement, un bouton « Annuler »
 *   et le bouton qui exécute. Rien ne part sans être passé par là.
 *
 *   POURQUOI UNE FENÊTRE, décidé par Charles le 2026-08-22, contre ce que
 *   j'avais écrit au plan. J'avais fabriqué un bouton à deux clics pour éviter
 *   la fenêtre. Une épreuve indépendante a mesuré que cette protection ne
 *   protégeait presque de rien : deux clics un peu espacés, trois clics parce
 *   qu'on ne voit pas le bouton changer, ou une touche Entrée maintenue —
 *   et le geste partait. Ses mots : « quand on clique pour supprimer, tu fais
 *   une fenêtre de confirmation. Qu'est-ce que tu t'emmerdes avec des
 *   double-clics ? » Il a raison, et pour une raison mécanique : **le bouton
 *   qui exécute n'existe pas au moment où l'on clique celui qui ouvre.** Aucun
 *   clic répété, aucune rafale de touche ne peut donc l'atteindre.
 *
 *   TROIS PRÉCAUTIONS, parce qu'une fenêtre mal faite est pire que pas de
 *   fenêtre : ① le curseur ENTRE dans la fenêtre, sur « Annuler » — le geste
 *   par défaut est celui qui ne détruit rien ; ② Échap ferme, c'est le réflexe
 *   de tout le monde et `dialog` le donne sans une ligne ; ③ à la fermeture, le
 *   curseur REVIENT sur le bouton d'où l'on venait, sans quoi quelqu'un qui
 *   navigue au clavier se retrouve rejeté en haut de la page.
 *
 * Ne fait pas : elle ne sait pas ce qu'elle déclenche, n'appelle aucune API et
 *   ne décide d'aucun droit. Elle ne sert QUE l'irréversible — un geste qui se
 *   rattrape d'un clic (fermer un accès, archiver) n'a rien à faire ici, sinon
 *   l'avertissement s'use et plus personne ne le lit.
 */

import { useId, useRef, type ReactNode } from 'react';

export function FenetreDeConfirmation({
  libelle,
  titre,
  libelleDeConfirmation,
  desactive = false,
  surConfirmation,
  children,
  ...reste
}: {
  /** Ce que dit le bouton qui ouvre la fenêtre. */
  readonly libelle: string;

  /** Le titre de la fenêtre — il NOMME ce qui va arriver, sans détour. */
  readonly titre: string;

  /** Ce que dit le bouton qui exécute, dans la fenêtre. */
  readonly libelleDeConfirmation: string;

  readonly desactive?: boolean;

  /** Appelé quand la personne confirme, et à ce moment-là seulement. */
  surConfirmation: () => void;

  /** L'avertissement lu dans la fenêtre : ce qui part, et ce qui reste. */
  readonly children: ReactNode;
}) {
  const fenetre = useRef<HTMLDialogElement>(null);
  const ouvreur = useRef<HTMLButtonElement>(null);
  const identifiantDuTitre = useId();

  const ferme = () => {
    fenetre.current?.close();

    // Le curseur revient d'où il venait. Sans cela, quelqu'un au clavier
    // repart du haut de la page et doit refaire tout le chemin.
    ouvreur.current?.focus();
  };

  return (
    <>
      <button
        ref={ouvreur}
        type="button"
        disabled={desactive}
        onClick={() => fenetre.current?.showModal()}
        {...reste}
      >
        {libelle}
      </button>

      <dialog
        ref={fenetre}
        aria-labelledby={identifiantDuTitre}
        className="fenetre-de-confirmation"
        // Échap passe par ici : `dialog` le donne, on ne fait que rendre le
        // curseur au bouton d'origine comme sur les deux autres sorties.
        onCancel={(evenement) => {
          evenement.preventDefault();
          ferme();
        }}
      >
        <h2 id={identifiantDuTitre}>{titre}</h2>

        <div data-role="avertissement">{children}</div>

        <p className="barre-de-boutons">
          {/*
           * « Annuler » EN PREMIER et porteur du curseur : le geste par défaut
           * doit être celui qui ne détruit rien. Quelqu'un qui ouvre la fenêtre
           * sans le vouloir, ou qui appuie sur Entrée par réflexe, n'efface
           * rien.
           */}
          <button type="button" autoFocus onClick={ferme} data-role="annuler">
            Annuler
          </button>

          <button
            type="button"
            className="message-erreur"
            data-role="confirmer"
            onClick={() => {
              ferme();
              surConfirmation();
            }}
          >
            {libelleDeConfirmation}
          </button>
        </p>
      </dialog>
    </>
  );
}
